/**
 * useFilteredSubmissions — Clean Architecture hook for Review Dashboard filtering.
 *
 * Encapsulates sub-tab filtering, global search, department filter,
 * date range filter, and In Review prioritization (oldest-first sort).
 */

import { useMemo, useState } from 'react';
import type { CampaignSubmission, Campaign } from '../types';
import { SubmissionStatus } from '../types';
import type { Department } from '../../../domain/entities';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ReviewSubTab = 'IN_REVIEW' | 'ALL' | 'COMPLETED' | 'REJECTED';

export interface ReviewFilterState {
  subTab: ReviewSubTab;
  searchQuery: string;
  departmentFilter: string;   // '' = all departments
  dateFrom: string;            // ISO date string or ''
  dateTo: string;              // ISO date string or ''
}

export interface SubTabCounts {
  inReview: number;
  all: number;
  completed: number;
  rejected: number;
}

export interface UseFilteredSubmissionsReturn {
  filters: ReviewFilterState;
  setSubTab: (tab: ReviewSubTab) => void;
  setSearchQuery: (q: string) => void;
  setDepartmentFilter: (dept: string) => void;
  setDateFrom: (d: string) => void;
  setDateTo: (d: string) => void;
  resetFilters: () => void;
  filtered: CampaignSubmission[];
  counts: SubTabCounts;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const INITIAL_FILTERS: ReviewFilterState = {
  subTab: 'IN_REVIEW',
  searchQuery: '',
  departmentFilter: '',
  dateFrom: '',
  dateTo: '',
};

/** Check if a submission is currently in the review pipeline. */
function isInPipeline(s: CampaignSubmission): boolean {
  return (
    s.status === SubmissionStatus.PENDING ||
    s.status === SubmissionStatus.IN_REVIEW
  );
}

/** Check if a submission has reached DONE / completed stage. */
function isCompleted(s: CampaignSubmission): boolean {
  return (
    s.status === SubmissionStatus.COMPLETED ||
    s.status === SubmissionStatus.DEPLOYMENT_READY ||
    s.currentStage === 'DONE'
  );
}

/**
 * Compute how long a submission has been sitting in its current stage (in ms).
 * Uses the most recent stage-changing action when finalized/rejected,
 * otherwise falls back to the most recent APPROVE action for in-flight reviews.
 */
export function computeTimeInStage(sub: CampaignSubmission): number {
  const now = Date.now();
  const sortedHistory = [...sub.reviewHistory].sort((a, b) => b.timestamp - a.timestamp);

  const isFinalized =
    sub.status === SubmissionStatus.REJECTED ||
    sub.status === SubmissionStatus.COMPLETED ||
    sub.status === SubmissionStatus.DEPLOYMENT_READY ||
    sub.currentStage === 'DONE';

  const stageEntryAction = isFinalized
    ? sortedHistory[0]
    : sortedHistory.find(a => a.action === 'APPROVE');

  const entryTs = stageEntryAction?.timestamp ?? sub.createdAt;
  return now - entryTs;
}

/**
 * Format milliseconds into a human-readable duration string.
 */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return 'Just now';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day';
  return `${days} days`;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useFilteredSubmissions(
  submissions: CampaignSubmission[],
  _campaigns: Campaign[],
  _departments: Department[],
): UseFilteredSubmissionsReturn {
  const [filters, setFilters] = useState<ReviewFilterState>({ ...INITIAL_FILTERS });

  // ── Counts (computed from entire unfiltered set) ──
  const counts = useMemo<SubTabCounts>(() => ({
    inReview:  submissions.filter(isInPipeline).length,
    all:       submissions.length,
    completed: submissions.filter(isCompleted).length,
    rejected:  submissions.filter(s => s.status === SubmissionStatus.REJECTED).length,
  }), [submissions]);

  // ── Filtered list ──
  const filtered = useMemo(() => {
    let result = [...submissions];

    // 1) Sub-tab filter
    switch (filters.subTab) {
      case 'IN_REVIEW':
        result = result.filter(isInPipeline);
        break;
      case 'COMPLETED':
        result = result.filter(isCompleted);
        break;
      case 'REJECTED':
        result = result.filter(s => s.status === SubmissionStatus.REJECTED);
        break;
      case 'ALL':
      default:
        // No filtering — show everything
        break;
    }

    // 2) Global search (campaign name, brand name, submitter name)
    if (filters.searchQuery.trim()) {
      const q = filters.searchQuery.trim().toLowerCase();
      result = result.filter(s =>
        s.campaignName.toLowerCase().includes(q) ||
        s.brandName.toLowerCase().includes(q) ||
        s.submitterName.toLowerCase().includes(q)
      );
    }

    // 3) Department filter (only meaningful for IN_REVIEW — filter by currentStage)
    if (filters.departmentFilter && filters.subTab === 'IN_REVIEW') {
      result = result.filter(s =>
        s.currentStage.toUpperCase() === filters.departmentFilter.toUpperCase()
      );
    }

    // 4) Date range filter (by createdAt)
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).getTime();
      result = result.filter(s => s.createdAt >= from);
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo).getTime() + 86400000 - 1; // end of day
      result = result.filter(s => s.createdAt <= to);
    }

    // 5) Sort: IN_REVIEW tab → oldest time-in-stage first (prioritize forgotten submissions)
    if (filters.subTab === 'IN_REVIEW') {
      result.sort((a, b) => computeTimeInStage(b) - computeTimeInStage(a));
    } else {
      // Default: newest first
      result.sort((a, b) => b.updatedAt - a.updatedAt);
    }

    return result;
  }, [submissions, filters]);

  // ── Setters ──
  const setSubTab = (tab: ReviewSubTab) => setFilters(p => ({ ...p, subTab: tab }));
  const setSearchQuery = (q: string) => setFilters(p => ({ ...p, searchQuery: q }));
  const setDepartmentFilter = (dept: string) => setFilters(p => ({ ...p, departmentFilter: dept }));
  const setDateFrom = (d: string) => setFilters(p => ({ ...p, dateFrom: d }));
  const setDateTo = (d: string) => setFilters(p => ({ ...p, dateTo: d }));
  const resetFilters = () => setFilters({ ...INITIAL_FILTERS });

  return {
    filters,
    setSubTab,
    setSearchQuery,
    setDepartmentFilter,
    setDateFrom,
    setDateTo,
    resetFilters,
    filtered,
    counts,
  };
}
